import React, { useRef, useState } from "react";

const pad = (n) => String(n).padStart(2, "0");
export const toMin = (t) => (t ? Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5)) : null);
export const toHHMM = (m) => `${pad(Math.floor(m / 60) % 24)}:${pad(m % 60)}`;
export const label12 = (t) => { const m = toMin(t); const h = Math.floor(m / 60) % 24; return `${h % 12 || 12}${m % 60 ? `:${pad(m % 60)}` : ""}${h < 12 ? "AM" : "PM"}`; };
export const halfHours = (from = 6, to = 22) => Array.from({ length: (to - from) * 2 }, (_, i) => ({ t: toHHMM(from * 60 + i * 30), free: true }));

/** Half-hour boxes: tap a start then tap an end, or drag across (mouse or finger). The range turns gold; tap again to start over. */
export default function TimeRangePicker({ slots = halfHours(), start, end, onChange, testid = "trp" }) {
  const [pending, setPending] = useState(null);
  const [drag, setDrag] = useState(null);
  const down = useRef(null);
  const idx = (t) => slots.findIndex((s) => s.t === t);
  const range = (a, b) => onChange(slots[Math.min(a, b)].t, toHHMM(toMin(slots[Math.max(a, b)].t) + 30));
  const at = (e) => { const el = document.elementFromPoint(e.clientX, e.clientY)?.closest("[data-slot]"); return el ? Number(el.dataset.slot) : null; };
  const onDown = (e, i) => { e.preventDefault(); down.current = i; setDrag([i, i]); e.currentTarget.releasePointerCapture?.(e.pointerId); };
  const onMove = (e) => { if (down.current == null) return; const i = at(e); if (i != null) setDrag([down.current, i]); };
  const onUp = (e) => {
    if (down.current == null) return;
    const a = down.current, b = at(e) ?? a;
    down.current = null; setDrag(null);
    if (a !== b) { setPending(null); range(a, b); return; }
    if (pending != null) { range(pending, a); setPending(null); } else { setPending(a); range(a, a); }
  };
  const s0 = drag ? Math.min(...drag) : start ? idx(start) : -1;
  const e0 = drag ? Math.max(...drag) : end ? slots.findIndex((s) => toMin(s.t) + 30 >= toMin(end)) : -1;
  const hint = pending != null ? `Start ${label12(slots[pending].t)} · now tap the last box` : start && end ? `${label12(start)} – ${label12(end)}` : "Tap a start time, then tap the end (or drag across)";
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <div className="grid gap-0.5 select-none touch-pan-y" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(44px, 1fr))" }}
        onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={(e) => down.current != null && e.buttons === 0 && onUp(e)}>
        {slots.map((s, i) => {
          const on = i >= s0 && i <= e0 && s0 >= 0;
          return (
            <button key={s.t} type="button" data-slot={i} onPointerDown={(e) => onDown(e, i)} title={s.title || label12(s.t)} data-testid={`${testid}-slot-${s.t.replace(":", "")}`}
              className={`h-10 text-[9px] font-mono leading-none flex flex-col items-center justify-center transition-colors ${on ? "bg-nex-gold text-nex-ink" : s.free ? "bg-emerald-500/20 hover:bg-emerald-500/40 text-emerald-100" : "bg-red-500/30 hover:bg-red-500/45 text-red-100"} ${pending === i ? "ring-2 ring-inset ring-white" : ""}`}>
              {label12(s.t)}{s.badge && <span className="opacity-80">{s.badge}</span>}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-nex-silver" data-testid={`${testid}-value`}>{hint}</p>
    </div>
  );
}
